/**
 * Exact decimal arithmetic on money strings, for the retailer screens.
 *
 * **Why this is not `packages/money`.** It should be. `@miraki/money` ships
 * CommonJS with no `import` condition, Vite does not pre-bundle it, and a
 * browser's native ESM loader cannot resolve named imports from a file with no
 * `export` statement — the build fails with *"MONEY_PATTERN is not exported"*.
 * `Shop.tsx`, `shop/Order.tsx` and `api/hooks.ts` each hit this independently
 * and each wrote their own copy. This is the third copy becoming the only copy;
 * the real fix is an ESM build of that package, which is a `packages/` change.
 *
 * Everything here is BigInt at NUMERIC(19,4)'s scale, in the spirit of NF-04:
 * never a JS float for a money **value**. It is also **display-only** — a cart
 * line never carries a price (invariant 6), so nothing computed here is ever
 * sent back to the API. A percentage for a CSS bar width is not a monetary
 * value and may use plain division.
 */

/** A money string to its integer representation at four decimal places. */
export function scaled(s: string): bigint {
  const neg = s.startsWith('-');
  const [whole, frac = ''] = s.replace('-', '').split('.');
  const n = BigInt(whole || '0') * 10000n + BigInt((frac + '0000').slice(0, 4));
  return neg ? -n : n;
}

function unscale(n: bigint): string {
  const neg = n < 0n;
  const abs = neg ? -n : n;
  const whole = abs / 10000n;
  const frac = (abs % 10000n).toString().padStart(4, '0');
  return `${neg ? '-' : ''}${whole}.${frac}`;
}

export function addMoney(a: string, b: string): string {
  return unscale(scaled(a) + scaled(b));
}

/** `a − b`, signed: a negative result is kept, not clamped. */
export function subMoney(a: string, b: string): string {
  return unscale(scaled(a) - scaled(b));
}

/** A unit price times an integer quantity — a line total. */
export function mulMoney(price: string, qty: number): string {
  return unscale(scaled(price) * BigInt(qty));
}

/**
 * `pct` percent of `amount` (`percentMoney('320.4000', '50')` is `160.2000`), rounded half
 * up at the fourth place, as the server's `percent` does. For a percentage of money, not a
 * ratio for a bar. Both inputs are non-negative decimal strings.
 */
export function percentMoney(amount: string, pct: string): string {
  const product = scaled(amount) * scaled(pct); // scale 10^8; dividing by 100 is dividing by 10^6 in all
  return unscale((product + 500000n) / 1000000n);
}

/** Sum of `price x qty` over many lines. Empty sums to zero, not to ''. */
export function sumLines(lines: ReadonlyArray<{ price: string; qty: number }>): string {
  return lines.reduce((total, l) => addMoney(total, mulMoney(l.price, l.qty)), '0.0000');
}

/**
 * How much of a limit a figure uses, as a percentage for a CSS width.
 *
 * A ratio for a bar, not a monetary value, so ordinary division is correct
 * here — and clamped, because a bar wider than its track is a rendering fault
 * rather than information.
 */
export function pctOf(projected: string, limit: string): number {
  const l = scaled(limit);
  if (l <= 0n) return 0;
  const pct = (Number(scaled(projected)) / Number(l)) * 100;
  return Math.min(100, Math.max(0, pct));
}
